import React, { useState, useEffect, useCallback } from 'react';
import { Clock, Save, Loader2, RotateCcw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DEFAULT_DAYS = DAY_LABELS.map((_, i) => ({
  day: i,
  open: '07:00',
  close: '23:00',
  closed: false,
}));

export default function WorkingHoursEditor({ token }) {
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [tzOffset, setTzOffset] = useState(3);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/config/working-hours`);
      if (!r.ok) return;
      const d = await r.json();
      const arr = (d?.days || []).slice(0, 7);
      while (arr.length < 7) arr.push({ ...DEFAULT_DAYS[arr.length] });
      setDays(arr.map((x, i) => ({
        day: i,
        open: x.open || '07:00',
        close: x.close || '23:00',
        closed: !!x.closed,
      })));
      if (typeof d?.timezone_offset === 'number') setTzOffset(d.timezone_offset);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/config/working-hours`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ timezone_offset: Number(tzOffset), days }),
      });
      if (!r.ok) {
        const t = await r.text();
        alert('Save failed: ' + t);
        return;
      }
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
      load();
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    if (!window.confirm('Reset working hours to default (Mon–Sun 07:00–23:00, GMT+3)?')) return;
    setDays(DEFAULT_DAYS.map((d) => ({ ...d })));
    setTzOffset(3);
  };

  const setField = (i, field, value) => {
    const next = [...days];
    next[i] = { ...next[i], [field]: value };
    setDays(next);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-12 text-center text-slate-600">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" />
        Loading working hours…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="working-hours-editor">
      <div className="flex items-start gap-3 p-4 bg-cyan-50 border border-cyan-200 rounded-xl">
        <Clock className="w-5 h-5 text-cyan-700 mt-0.5" />
        <div className="text-sm text-cyan-900">
          <p className="font-semibold mb-1">Global Working Hours</p>
          <p className="text-cyan-800">
            These hours drive priority availability across the app. Each priority can additionally
            apply <em>time-to-open</em>, <em>time-to-close</em> offsets and per-day disable windows
            (configured in <strong>Order Priorities</strong>).
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h3 className="font-bold text-slate-800">Schedule</h3>
            <p className="text-xs text-slate-500 mt-0.5">Tick "Closed" to mark the day as a day off.</p>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase">Timezone (GMT±)</label>
            <input
              type="number"
              min="-12"
              max="14"
              step="1"
              value={tzOffset}
              onChange={(e) => setTzOffset(parseInt(e.target.value || 0, 10))}
              className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono"
              data-testid="working-hours-tz"
            />
          </div>
        </div>

        <div className="space-y-2">
          {days.map((d, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 p-3 rounded-lg border ${d.closed ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200'}`}
              data-testid={`wh-day-${i}`}
            >
              <span className="w-12 font-bold text-sm text-slate-700">{DAY_LABELS[i]}</span>
              <input
                type="time"
                value={d.open}
                disabled={d.closed}
                onChange={(e) => setField(i, 'open', e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded text-sm disabled:opacity-50"
                data-testid={`wh-open-${i}`}
              />
              <span className="text-slate-400">→</span>
              <input
                type="time"
                value={d.close}
                disabled={d.closed}
                onChange={(e) => setField(i, 'close', e.target.value)}
                className="px-2 py-1.5 border border-slate-300 rounded text-sm disabled:opacity-50"
                data-testid={`wh-close-${i}`}
              />
              <label className="ml-auto flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={d.closed}
                  onChange={(e) => setField(i, 'closed', e.target.checked)}
                  data-testid={`wh-closed-${i}`}
                  className="w-4 h-4 accent-rose-500"
                />
                <span className={d.closed ? 'text-rose-700 font-semibold' : 'text-slate-500'}>
                  {d.closed ? 'Closed' : 'Open'}
                </span>
              </label>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-4 mt-4 border-t border-slate-200">
          <button
            onClick={save}
            disabled={saving}
            data-testid="working-hours-save"
            className="px-5 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 hover:shadow-md flex items-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save working hours
          </button>
          <button
            onClick={resetDefaults}
            className="px-5 py-2 bg-white border border-slate-300 rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2"
            data-testid="working-hours-reset"
          >
            <RotateCcw className="w-4 h-4" /> Reset defaults
          </button>
          {savedFlash && (
            <span className="self-center text-emerald-600 text-sm font-medium" data-testid="working-hours-saved">
              ✓ Saved
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
